import type { MathTopic, Difficulty, QuestionType, AcademicLevel, EducationSystem, GeneratorConfig, GeneratedQuestion } from "@/lib/math-taxonomy/types";

export type { MathTopic, Difficulty, QuestionType, AcademicLevel, EducationSystem, GeneratorConfig, GeneratedQuestion };

/**
 * Topics with a working deterministic generator, and the difficulty /
 * question-type surface each one genuinely supports. The studio UI filters
 * its dropdowns through this map so users can never select a combination
 * the engine cannot serve. A unit test asserts this map matches the
 * template registry, so adding a template with new coverage without
 * updating the map fails loudly instead of drifting silently.
 */
export const TOPIC_CAPABILITIES: Record<
  string,
  { difficulties: Difficulty[]; questionTypes: QuestionType[] }
> = {
  arithmetic: {
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "fill-in-the-blank",
      "word-problem",
      "calculation",
      "mixed",
    ],
  },
  algebra: {
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "fill-in-the-blank",
      "proof",
      "calculation",
      "derivation",
      "mixed",
    ],
  },
  geometry: {
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "calculation",
      "mixed",
    ],
  },
  calculus: {
    difficulties: ["easy", "medium", "hard", "expert"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "calculation",
      "derivation",
      "mixed",
    ],
  },
  trigonometry: {
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "calculation",
      "word-problem",
      "mixed",
    ],
  },
  statistics: {
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "calculation",
      "word-problem",
      "mixed",
    ],
  },
  probability: {
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [
      "multiple-choice",
      "short-answer",
      "numerical-answer",
      "calculation",
      "word-problem",
      "mixed",
    ],
  },
};

export function isTopicImplemented(topic: string): boolean {
  return topic in TOPIC_CAPABILITIES;
}

export interface TemplateParams {
  [key: string]: number | string;
}

export interface QuestionTemplate {
  id: string;
  topic: MathTopic;
  subtopic: string;
  minLevel: AcademicLevel;
  difficulties: Difficulty[];
  questionTypes: QuestionType[];
  params: {
    [paramName: string]: {
      type: "int" | "float" | "choice";
      min?: number;
      max?: number;
      choices?: (number | string)[];
      exclude?: (number | string)[];
    };
  };
  generate: (params: TemplateParams, seed: number) => {
    question: string;
    latex?: string;
    answer: string;
    solution?: string;
  };
  validate: (params: TemplateParams) => { valid: boolean; reason?: string };
}

export interface GeneratorContext {
  config: GeneratorConfig;
  seed: number;
  previousQuestions: GeneratedQuestion[];
  rng: () => number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  similarTo?: string;
  similarity: number;
}

export interface GenerationResult {
  questions: GeneratedQuestion[];
  seed: number;
  stats: {
    generated: number;
    rejected: number;
    duplicates: number;
    timeMs: number;
  };
}

export type GeneratorFn = (context: GeneratorContext) => Promise<GeneratedQuestion[]>;